import React, { useState, useCallback, useEffect } from 'react';
import { useGlobalContext } from '../../contexts';


export function ContentModalPostscriptForm({
  postscriptForm,
  linkText = '',
  email = '',
  responseStatus,
  setResponseStatus,
}) {
  const {
    state: { modal },
    actions: { closeModal },
  } = useGlobalContext();

  const [phone, setPhone] = useState('');
  const [properties, setProperties] = useState(() => {
    if (postscriptForm?.properties) {
      return postscriptForm.properties.reduce((acc, prop) => {
        acc[prop.name] = prop.value || '';
        return acc;
      }, {});
    }
    return {};
  });
  const [statusMessage, setStatusMessage] = useState('');

  const handlePhoneChange = useCallback((e) => {
    setPhone(e.target.value);
  }, []);

  const handlePropertiesChange = useCallback((e) => {
    const { name, value } = e.target;
    setProperties((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  // Sync properties with linkText for passLinkTitle fields
  useEffect(() => {
    if (postscriptForm?.properties) {
      setProperties((prev) => {
        const updated = { ...prev };
        postscriptForm.properties.forEach((prop) => {
          if (prop.passLinkTitle) {
            updated[prop.name] = linkText;
          }
        });
        return updated;
      });
    }
  }, [linkText, postscriptForm]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Filter out empty properties
      const filteredProperties = Object.fromEntries(
        Object.entries(properties).filter(
          ([_, value]) => value !== '' && value !== undefined && value !== null
        )
      );

      try {
        const endpoint = '/api/postscript/customSMSForm';
        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'subscribeToSMS',
            keyword: postscriptForm?.keywordId,
            phone_number: phone,
            email: email,
            properties: filteredProperties,
          }),
        };

        const response = await fetch(endpoint, options);
        if (response.ok && response.status === 200) {
          setStatusMessage('Thank you for subscribing!');
          setResponseStatus('success');
        } else {
          const errorData = await response.json();
          let errorMsg = 'Failed to subscribe. Please try again later.';
          if (errorData && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            // Join all error messages
            errorMsg = errorData.errors.map(e => e.msg || e.detail || e.title).filter(Boolean).join(' | ');
          } else if (typeof errorData === 'string') {
            errorMsg = errorData;
          } else if (errorData && errorData.msg) {
            errorMsg = errorData.msg;
          }
          if (errorMsg && errorMsg.includes('Subscriber already exists')) {
            setStatusMessage('Thank you for subscribing!');
            setResponseStatus('success');
          } else {
            setStatusMessage(errorMsg);
            setResponseStatus('error');
          }
        }
      } catch (error) {
        console.error('Subscription error:', error.message);
        setStatusMessage('Failed to subscribe. Please try again later.');
        setResponseStatus('error');
      }
    },
    [phone, properties, postscriptForm]
  );

  return (
    <>
      {postscriptForm?.title && (
        <h3 className="text-lg font-bold text-center">
          {postscriptForm.title}
        </h3>
      )}
      {postscriptForm?.text && (
        <p className="text-center font-medium mt-2.5">
          {postscriptForm.text}
        </p>
      )}
      <form className="flex flex-col gap-1 pt-2.5" onSubmit={handleSubmit}>
        <label htmlFor="newsletterPhone">
          Phone: 
        </label>
        <input
          id="gameDayNewsletterPhone"
          className="input-text text-text"
          name="gameDayNewsletterPhone"
          autoComplete="phone"
          type="phone"
          placeholder="Enter your phone"
          value={phone}
          onChange={handlePhoneChange}
          required
          aria-required="true"
          aria-label="Phone address for subscription"
        />

        {postscriptForm?.properties?.map((property, index) => (
          <React.Fragment key={property?.name || index}>
            <label className="sr-only" htmlFor={`property_${property.name}`}>
              {property.name}:
            </label>
            <input
              id={`property_${property.name}`}
              className={`input-text text-text ${
                property.hidden ? 'sr-only' : ''
              }`}
              name={property.name}
              type={property.type || 'text'}
              placeholder={property.placeholder || `Enter your ${property.name}`}
              value={
                property.passLinkTitle
                  ? linkText
                  : properties[property.name] || ''
              }
              onChange={handlePropertiesChange}
              readOnly={property.passLinkTitle}
              hidden={property.hide}
              aria-label={property.name}
            />
          </React.Fragment>
        ))}

        <button
          aria-label="Subscribe to notifications"
          className="btn-primary mt-1.5 w-full"
          type="submit"
        >
          Subscribe
        </button>

        <p className="text-xs text-center mt-2.5">
          By subscribing, you agree to receive recurring auto-dialed marketing SMS (including cart reminders; AI content; artificial or prerecorded voices) and our <a className="underline" href="https://terms.pscr.pt/legal/shop/rhoback/terms_of_service" target='_blank' rel="noopener noreferrer">Terms of Service</a> (including arbitration) from Rhoback. Consent is not required to purchase. Msg & data rates may apply. Msg frequency varies. Reply HELP for help; STOP to opt-out. View <a className="underline" href="https://terms.pscr.pt/legal/shop/rhoback/privacy_policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        {statusMessage && (
          <p
            className={`mt-2.5 px-4 py-2 text-base transition-all md:text-sm ${
              responseStatus === 'error'
                ? 'font-semibold border border-red-300 bg-red-100 text-red-800'
                : 'font-semibold border border-green-200 bg-green-50 text-current text-green-800'
            }`}
            role={responseStatus === 'error' ? 'alert' : undefined}
          >
            {statusMessage}
          </p>
        )}
      </form>
      <button
        aria-label="Close modal"
        className="btn-secondary mt-2.5 w-full"
        onClick={closeModal}
        type="button"
      >
        Close
      </button>
    </>
  );
}

ContentModalPostscriptForm.displayName = 'ContentModalPostscriptForm';
